'use client';

import { useEffect, useState } from 'react';
import { createClient } from '@/lib/supabase/client';
import ReviewSubmissionForm from './ReviewSubmissionForm';

interface Review {
  id: string;
  reviewer_name: string;
  rating: number;
  review_text: string;
}

export default function TestimonialSection() {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const supabase = createClient();
        const { data, error } = await supabase
          .from('reviews')
          .select('id, reviewer_name, rating, review_text')
          .eq('status', 'approved')
          .order('submitted_at', { ascending: false })
          .limit(9);

        if (!error && data) {
          setReviews(data);
        }
      } catch (err) {
        // Silently fail — section just won't show reviews
      } finally {
        setLoading(false);
      }
    };

    fetchReviews();
  }, []);

  return (
    <section id="client-reviews" className="testimonial-section section-space" aria-labelledby="testimonial-heading">
      <div className="site-container">
        <div className="section-heading-row testimonial-heading">
          <div>
            <p className="eyebrow">Client testimonials</p>
            <h2 id="testimonial-heading">Words from <em>Clients</em></h2>
          </div>
          <div className="testimonial-heading-meta">
            <p>Verified client feedback, 2020–2025.</p>
            <button
              onClick={() => setShowForm(!showForm)}
              className="text-link testimonial-link"
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
            >
              Share your experience
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M7 7h10v10" />
                <path d="M7 17 17 7" />
              </svg>
            </button>
          </div>
        </div>

        {/* Review submission form */}
        {showForm && (
          <div style={{
            background: 'rgba(185,152,95,0.05)',
            border: '1px solid rgba(185,152,95,0.2)',
            borderRadius: '12px',
            padding: '32px',
            marginBottom: '48px',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>Share Your Experience</h3>
              <button
                onClick={() => setShowForm(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#7a6a55', fontSize: '20px', lineHeight: 1 }}
                aria-label="Close form"
              >
                ×
              </button>
            </div>
            <ReviewSubmissionForm />
          </div>
        )}

        {/* Reviews grid */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px 0', color: '#7a6a55' }}>Loading reviews…</div>
        ) : reviews.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 0', color: '#7a6a55' }}>
            No reviews yet. Be the first to share your experience.
          </div>
        ) : (
          <div className="testimonial-grid">
            {reviews.map((review) => (
              <figure key={review.id} className="testimonial-card" aria-label={`Client testimonial ${review.reviewer_name}`}>
                <p className="testimonial-stars" aria-label={`${review.rating} out of five stars`}>
                  {'★'.repeat(review.rating)}{'☆'.repeat(5 - review.rating)}
                </p>
                <blockquote>&ldquo;{review.review_text}&rdquo;</blockquote>
                <figcaption>
                  <span>Client testimonial</span>
                  <strong>{review.reviewer_name}</strong>
                </figcaption>
              </figure>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
